"use client";

import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import "./animalia.css";
import { CURATED_THEMES } from "./tokens";
import AniButton from "./primitives/AniButton";
import AniIcon from "./primitives/AniIcon";
import AniCalendar from "./blocks/AniCalendar";
import AniTaskCard from "./blocks/AniTaskCard";
import AniChart from "./blocks/AniChart";
import AniSearch from "./blocks/AniSearch";
import { XrayOverlay, XrayToolbar, useXrayInspection, type XrayMode } from "./XrayInspector";
import { xrayTheme } from "./xrayTheme";

const CHART_DATA = Array.from({ length: 7 }, (_, day) =>
  Array.from({ length: 8 }, (_, hour) => (day * 3 + hour * 7 + day * hour) % 11),
);

/** Five repeatable skins on the same components, with inspection entirely in place. */
const AnimaliaPlayground = () => {
  const scopeRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [themeIndex, setThemeIndex] = useState(0);
  const theme = CURATED_THEMES[themeIndex];
  const [xray, setXray] = useState(false);
  const [xrayMode, setXrayMode] = useState<XrayMode>("Components");
  const panelId = useId();
  const inspection = useXrayInspection(gridRef, xray, xrayMode);
  const renderedTheme = useMemo(() => xray ? xrayTheme(theme) : theme, [theme, xray]);
  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    const sync = () => scopeRef.current?.style.setProperty("--ani-bleed", `${document.documentElement.clientWidth}px`);
    sync(); window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, []);

  const nextTheme = () => {
    const next = (themeIndex + 1) % CURATED_THEMES.length;
    setThemeIndex(next);
    setAnnouncement(`${CURATED_THEMES[next].label}, theme ${next + 1} of ${CURATED_THEMES.length}. ${CURATED_THEMES[next].fontLabel}.`);
  };

  return <div>
    <div ref={scopeRef} data-ani-scope data-ani-theme={theme.label}
      data-ani-xray={xray ? "on" : "off"} data-ani-layer={xrayMode.toLowerCase()}
      style={renderedTheme.vars as React.CSSProperties}>
      <div className="ani-stage ani-tint">
        <div className="ani-stage-inner">
          <div className="ani-playground-head">
            {/* Rendered in both states — hidden, never removed — so turning X-ray
                on cannot shift the controls row or the toggle in it. */}
            <h3 className="ani-xray-title" aria-hidden={!xray}>inspection mode</h3>
            <div className="ani-playground-controls">
            {xray ? <XrayToolbar mode={xrayMode} onMode={setXrayMode} panelId={panelId}
              onExit={() => { setXray(false); setAnnouncement("X-ray off."); }} /> :
              <div className="ani-theme-controls">
                <AniButton onClick={nextTheme} aria-describedby={`${panelId}-theme`}>
                  <AniIcon name="figma-create" />Try Me
                </AniButton>
                <span className="sr-only" id={`${panelId}-theme`}>{theme.label} · {themeIndex + 1}/{CURATED_THEMES.length}</span>
              </div>}
            {!xray && <AniButton variant="tertiary" iconOnly aria-pressed={xray}
              aria-label={xray ? "Exit X-ray mode" : "Enter X-ray mode"}
              onClick={() => { setXray(value => !value); setAnnouncement(xray ? "X-ray off." : "X-ray on. Hover or focus a component to inspect it."); }}>
              <AniIcon name="detective" />
            </AniButton>}
            </div>
          </div>
          <div className="ani-inspection-stage" id={panelId} role={xray ? "tabpanel" : undefined}
            aria-labelledby={xray ? `${panelId}-${xrayMode}` : undefined}>
            <div ref={gridRef} className="ani-grid" data-structure="Playground grid">
              <div className="ani-column" data-structure="Calendar column"><AniCalendar /></div>
              <div className="ani-column" data-structure="Task column"><AniTaskCard /></div>
              <div className="ani-column ani-chart-column" data-structure="Chart column"><AniChart data={CHART_DATA} theme={renderedTheme} /></div>
              <div className="ani-column ani-search-column" data-structure="Search column"><AniSearch /></div>
            </div>
            {xray && <XrayOverlay items={inspection.items} mode={xrayMode} selected={inspection.selected} theme={theme} />}
          </div>
        </div>
      </div>
    </div>
    <p className="sr-only" role="status" aria-live="polite">{announcement}</p>
  </div>;
};

export default AnimaliaPlayground;
